import { Grid, TextField, Button, Snackbar, Alert, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getUser, register } from "../../../Redux/Auth/Action";
import { useEffect, useRef, useState } from "react";
import PasswordField from "./PasswordField";
import useSiteSettings from "../../../hooks/useSiteSettings";

// Same rules the API enforces (server/src/services/user.service.js). Duplicated
// deliberately: this gives instant feedback, the server is the boundary.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MIN_PASSWORD = 6;
const MAX_NAME = 50;

// Tolerate +91 / leading 0 and any separators, then require a 10-digit Indian
// mobile (they always begin 6-9, so this catches a dropped or extra digit).
const normalizeMobile = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
  return digits;
};

const validate = ({ firstName, lastName, email, phone, password, confirm }) => {
  const e = {};
  if (!firstName.trim()) e.firstName = "First name is required.";
  else if (firstName.trim().length > MAX_NAME) e.firstName = `Keep it under ${MAX_NAME} characters.`;

  if (!lastName.trim()) e.lastName = "Last name is required.";
  else if (lastName.trim().length > MAX_NAME) e.lastName = `Keep it under ${MAX_NAME} characters.`;

  if (!email.trim()) e.email = "Email is required.";
  else if (!EMAIL_RE.test(email.trim())) e.email = "Enter a valid email address.";

  if (!phone.trim()) e.phone = "Mobile number is required.";
  else if (!/^[6-9]\d{9}$/.test(normalizeMobile(phone)))
    e.phone = "Enter a valid 10-digit mobile number.";

  if (!password) e.password = "Password is required.";
  else if (password.length < MIN_PASSWORD)
    e.password = `Password must be at least ${MIN_PASSWORD} characters.`;

  if (!confirm) e.confirm = "Please re-enter your password.";
  else if (password !== confirm) e.confirm = "Passwords do not match.";

  return e;
};

export default function RegisterUserForm({ setPageType }) {
  const dispatch = useDispatch();
  const brand = useSiteSettings().brandName || "";
  const [openSnackBar, setOpenSnackBar] = useState(false);
  // All fields controlled so an error can be cleared as soon as it's corrected.
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  // Per-field messages, so the shopper sees WHICH field is wrong rather than one
  // generic line under the form.
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");

  const setField = (name) => (e) => {
    setForm((f) => ({ ...f, [name]: e.target.value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };
  // Local, not auth.isLoading — that is also raised by getUser on mount.
  const [submitting, setSubmitting] = useState(false);
  // Synchronous guard: setSubmitting is async, so two fast clicks would both
  // pass a state-based check and send two signup requests.
  const inFlight = useRef(false);
  const { auth } = useSelector((store) => store);
  const handleClose = () => setOpenSnackBar(false);

  // Only complain once they've actually started typing the confirmation,
  // otherwise the field is red from the moment it renders.
  const mismatch = confirm.length > 0 && password !== confirm;

  const jwt = localStorage.getItem("jwt");

  useEffect(() => {
    if (jwt) {
      dispatch(getUser(jwt));
    }
  }, [dispatch, jwt]);

  useEffect(() => {
    if (auth.user || auth.error) setOpenSnackBar(true);
  }, [auth.user, auth.error]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (inFlight.current) return;
    setError("");

    const found = validate({ ...form, password, confirm });
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    // confirmPassword is a client-side typo guard only — it is deliberately
    // not sent to the API. Values are trimmed so a stray space can't create an
    // account the customer then can't sign in to.
    const userData = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      password,
    };
    inFlight.current = true;
    setSubmitting(true);
    await dispatch(register(userData));
    // Reset regardless: a rejected signup (duplicate email/phone) must leave
    // the form usable so they can correct it and retry.
    inFlight.current = false;
    setSubmitting(false);
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="font-display text-3xl font-bold text-[#511d00]">
          Create Account
        </h2>
        <p className="mt-1 text-sm text-[#8a7575]">
          {brand ? `Join the ${brand} family.` : "Create your account."}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              id="firstName"
              name="firstName"
              label="First Name"
              fullWidth
              value={form.firstName}
              onChange={setField("firstName")}
              error={Boolean(errors.firstName)}
              helperText={errors.firstName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              id="lastName"
              name="lastName"
              label="Last Name"
              fullWidth
              value={form.lastName}
              onChange={setField("lastName")}
              error={Boolean(errors.lastName)}
              helperText={errors.lastName}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              id="email"
              name="email"
              type="email"
              label="Email"
              fullWidth
              autoComplete="email"
              value={form.email}
              onChange={setField("email")}
              error={Boolean(errors.email)}
              helperText={errors.email}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              id="phone"
              name="phone"
              type="tel"
              label="Mobile Number"
              fullWidth
              autoComplete="tel"
              inputProps={{ inputMode: "numeric", maxLength: 16 }}
              value={form.phone}
              onChange={setField("phone")}
              error={Boolean(errors.phone)}
              helperText={errors.phone}
            />
          </Grid>
          <Grid item xs={12}>
            <PasswordField
              required
              id="password"
              name="password"
              label="Password"
              fullWidth
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors((p) => ({ ...p, password: undefined }));
              }}
              error={Boolean(errors.password)}
              helperText={errors.password}
            />
          </Grid>
          <Grid item xs={12}>
            <PasswordField
              required
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm Password"
              fullWidth
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                setErrors((p) => ({ ...p, confirm: undefined }));
              }}
              error={mismatch || Boolean(errors.confirm)}
              helperText={
                mismatch ? "Passwords do not match." : errors.confirm
              }
            />
          </Grid>
          {error && (
            <Grid item xs={12}>
              <p className="text-sm font-medium text-[#c0392b]">{error}</p>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              className="w-full"
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting}
              sx={{ py: 1.3, borderRadius: 2, fontWeight: 700 }}
            >
              {submitting ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "Sign Up"
              )}
            </Button>
          </Grid>
        </Grid>
      </form>

      <div className="mt-5 flex items-center justify-center gap-1 text-sm text-[#5e3e3e]">
        <span>Already have an account?</span>
        <button
          onClick={() => setPageType("login")}
          className="font-semibold text-[#b28585] underline-offset-2 hover:underline"
        >
          Sign In
        </button>
      </div>

      <Snackbar open={openSnackBar} autoHideDuration={6000} onClose={handleClose}>
        <Alert
          onClose={handleClose}
          severity={auth.error ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {auth.error ? auth.error : auth.user ? "Register Success" : ""}
        </Alert>
      </Snackbar>
    </div>
  );
}
